import React, { ReactNode, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ExpandableProps {
  isExpanded: boolean;
  size: { width: string; height: string };
  onResize: (newWidth: string) => void;
  title: any;
  children: ReactNode;
  closeContent: () => void;
}

const Expandable: React.FC<ExpandableProps> = ({
  isExpanded,
  size,
  onResize,
  title,
  children,
  closeContent,
}) => {
  const resizableRef = useRef<HTMLDivElement | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!resizableRef.current) return;

    const startX = e.clientX;
    const startWidth = resizableRef.current.offsetWidth;

    const minWidth = Math.min(window.innerWidth * 0.4, 800); 
    const maxWidth = window.innerWidth + 3;

    const doDrag = (e: MouseEvent) => {
      const newWidth = startWidth - (e.clientX - startX);
      if (newWidth > minWidth && newWidth < maxWidth) {
        onResize(newWidth + 'px');
      }
    };

    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  };

  return (
    <>
      {isExpanded && (
        <div
          ref={resizableRef}
          className="absolute top-0 right-0 bg-white p-4 shadow-lg z-20 overflow-y-auto"
          style={{ width: size.width, height: size.height }}
        >
          <div className="flex justify-between items-start">
            <h2 className="text-lg font-bold">{title}</h2>
            <button
              onClick={closeContent}
              className="flex rounded-md p-1 border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {children}

          <div
            className="cursor-ew-resize h-20 w-2 bg-gray-300 absolute left-0 top-1/2 transform -translate-y-1/2 rounded"
            onMouseDown={handleMouseDown}
          />
        </div>
      )}
    </>
  );
};

export default Expandable;
